import {
  MESSAGE_ASSET_MAX_BYTES,
  MESSAGE_IMAGE_MAX_EDGE,
  MESSAGE_IMAGE_TARGET_BYTES,
  WEDDING_PHOTO_MAX_BYTES,
  WEDDING_PHOTO_MAX_EDGE,
  WEDDING_PHOTO_TARGET_BYTES,
} from '@vendorops/shared';

/**
 * Re-encodes a dropped photograph into something that can be emailed.
 *
 * A planner drags a picture straight off their phone or their photographer's
 * gallery, and that file is 3-5MB at 4000px. Six of them is a message no
 * vendor's mail server accepts — Gmail's hard limit is 25MB on the assembled
 * message, and base64 adds a third on top before it is counted. The vendor
 * who pays for the difference is the one reading on a phone in a car park
 * between two site visits.
 *
 * So the resize happens here, in the browser, before the upload: it costs the
 * planner nothing (the bytes never leave their machine at full size), it makes
 * the upload itself fast on a bad connection, and it means the server's own
 * ceiling is a backstop rather than a routine rejection a planner has to
 * understand.
 *
 * What is deliberately *not* done: a second "send full quality" path. It would
 * reintroduce exactly the failure this prevents, and 1600px is already wider
 * than any mail client renders an inline image.
 */

const ACCEPTED = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];

/** Tried in order until one lands under the target. The last is used whatever
 *  it weighs — a photograph of noise does not compress, and refusing it at
 *  that point would be refusing the planner's actual picture. */
const QUALITY_STEPS = [0.82, 0.7, 0.6, 0.5];

export class ImageRejected extends Error {}

export interface PreparedImage {
  file: File;
  width: number;
  height: number;
  /** True when the bytes were re-encoded, so the caller can say so once. */
  resized: boolean;
}

/**
 * Animation is the reason GIFs are passed through rather than re-encoded: a
 * canvas holds one frame, so resizing a GIF silently turns it into a still.
 * A planner who dropped an animation and got a photograph back would have no
 * way to tell until a vendor mentioned it.
 */
function isAnimatedFormat(type: string): boolean {
  return type === 'image/gif';
}

async function decode(file: File): Promise<{ bitmap: ImageBitmap | HTMLImageElement; width: number; height: number }> {
  if (typeof createImageBitmap === 'function') {
    const bitmap = await createImageBitmap(file);
    return { bitmap, width: bitmap.width, height: bitmap.height };
  }

  // Safari versions that cannot decode a Blob this way still load one through
  // an <img>. The object URL is revoked either way, including on failure.
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new ImageRejected('That image could not be read.'));
      el.src = url;
    });
    return { bitmap: img, width: img.naturalWidth, height: img.naturalHeight };
  } finally {
    URL.revokeObjectURL(url);
  }
}

function toBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
}

/**
 * The three numbers and the two sentences that differ between one use of this
 * resizer and another.
 *
 * Extracted when the couple photo became the second caller. The mechanics below
 * — the decode, the white matte, the quality ladder, the "a re-encoded PNG can
 * come out larger" guard — are identical for both; only the ceilings and the
 * wording a planner reads are not, and a second copy of the mechanics to vary
 * two strings would be the wrong trade.
 */
export interface ImageLimits {
  maxEdge: number;
  targetBytes: number;
  maxBytes: number;
  /** Shown when the bytes are past `maxBytes` even after re-encoding. */
  tooLarge: string;
  /** Shown when an animated original is past `maxBytes` and cannot be resized. */
  animatedTooLarge: string;
}

const EMAIL_LIMITS: ImageLimits = {
  maxEdge: MESSAGE_IMAGE_MAX_EDGE,
  targetBytes: MESSAGE_IMAGE_TARGET_BYTES,
  maxBytes: MESSAGE_ASSET_MAX_BYTES,
  tooLarge: 'That image is too large to email, even resized.',
  animatedTooLarge: 'That GIF is too large to email. Try a shorter or smaller one.',
};

const PHOTO_LIMITS: ImageLimits = {
  maxEdge: WEDDING_PHOTO_MAX_EDGE,
  targetBytes: WEDDING_PHOTO_TARGET_BYTES,
  maxBytes: WEDDING_PHOTO_MAX_BYTES,
  tooLarge: 'That photo is too large, even resized. Try a smaller one.',
  animatedTooLarge: 'That GIF is too large. Try a shorter or smaller one.',
};

/** The couple's portrait for the weddings grid. */
export function prepareCouplePhoto(file: File): Promise<PreparedImage> {
  return prepareImage(file, PHOTO_LIMITS);
}

export async function prepareImageForEmail(file: File): Promise<PreparedImage> {
  return prepareImage(file, EMAIL_LIMITS);
}

async function prepareImage(file: File, limits: ImageLimits): Promise<PreparedImage> {
  if (!ACCEPTED.includes(file.type)) {
    throw new ImageRejected('That file is not a PNG, JPEG, GIF, or WebP image.');
  }

  if (isAnimatedFormat(file.type)) {
    if (file.size > limits.maxBytes) throw new ImageRejected(limits.animatedTooLarge);
    const { width, height } = await decode(file);
    return { file, width, height, resized: false };
  }

  const { bitmap, width, height } = await decode(file);
  const longest = Math.max(width, height);
  const scale = longest > limits.maxEdge ? limits.maxEdge / longest : 1;

  // Already small in both senses: nothing to gain, and re-encoding a PNG as
  // JPEG here would throw away transparency for no reason.
  if (scale === 1 && file.size <= limits.targetBytes) {
    return { file, width, height, resized: false };
  }

  const targetWidth = Math.max(1, Math.round(width * scale));
  const targetHeight = Math.max(1, Math.round(height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new ImageRejected('That image could not be resized.');

  // White underneath, because the output is JPEG and JPEG has no transparency:
  // a logo with an alpha channel would otherwise composite onto black.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, targetWidth, targetHeight);
  ctx.drawImage(bitmap as CanvasImageSource, 0, 0, targetWidth, targetHeight);

  let encoded: Blob | null = null;
  for (const quality of QUALITY_STEPS) {
    encoded = await toBlob(canvas, quality);
    if (encoded && encoded.size <= limits.targetBytes) break;
  }
  if (!encoded) throw new ImageRejected('That image could not be resized.');

  // A small PNG screenshot can come out of the JPEG encoder *larger* than it
  // went in. Keeping the original in that case is both smaller and sharper.
  if (encoded.size >= file.size && file.size <= limits.maxBytes && scale === 1) {
    return { file, width, height, resized: false };
  }

  if (encoded.size > limits.maxBytes) throw new ImageRejected(limits.tooLarge);

  const renamed = file.name.replace(/\.[^.]+$/, '') || 'image';
  return {
    file: new File([encoded], `${renamed}.jpg`, { type: 'image/jpeg' }),
    width: targetWidth,
    height: targetHeight,
    resized: true,
  };
}
